/**
 * Opt-in sync (rule 5: nothing uploads before the person says yes).
 *
 * Every network path here checks consent first, at call time, from the stored settings:
 *   - sync (POST /v1/sync, identity creation, pairing)  needs settings.sync.enabled
 *   - sharing ratings (POST /v1/availability/votes)     needs settings.sync.share_votes
 *   - community ratings (GET /v1/availability)          needs either (it names a region)
 *
 * One round: push what is dirty (everything, on the first sync of an identity) with the last
 * `server_time` as `since`, at most MAX_PUSH records a request (the worker refuses more);
 * apply what comes back by last-writer-wins (trips merge by section); follow `cursor` until the server has nothing
 * more; store the first page's `server_time`. Each pushed record gets its own answer: the
 * server's copy replaces ours when it won (`stale`) or changed our stamp (`clamped`); a
 * `failed` or `rejected` record stays dirty for the next round, and a rejection is shown.
 * Offline is a normal state, not an error: the dirty marks stay and the next round sends them.
 */
import type { AvailabilityVote, RegionCode, SyncRecord } from '@resupport/core'
import type { PushResult } from '../api'
import { Api, ApiError } from '../api'
import { timeOf } from '../engine'
import type { Store } from '../store/store'
import { findRegion } from '../store/settings'

export type SyncStatus = 'off' | 'idle' | 'syncing' | 'offline' | 'error'

export interface SyncRuntime {
  status: SyncStatus
  error?: string
  /** Records applied from other devices in the last round. */
  pulled?: number
  pushed?: number
}

/** Pages of a single pull before giving up for this round. */
const MAX_PAGES = 50

/** Records the worker takes in one request (worker/src/routes/sync.ts `MAX_PUSH`). */
export const MAX_PUSH = 100

function slices<T>(list: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size))
  return out
}

/** The first thing a rejection says, for the sync sheet. */
function rejectionMessage(result: PushResult): string {
  const issue = result.issues?.find((i) => i.message)
  const what = result.kind ? `A ${result.kind}` : 'A change'
  return issue ? issue.message : `${what} was refused by the server.`
}

export class SyncClient {
  private runtime: SyncRuntime
  private listeners = new Set<() => void>()
  private running: Promise<SyncRuntime> | null = null

  constructor(
    private readonly store: Store,
    private readonly api: Api = new Api(),
  ) {
    this.runtime = { status: store.getState().settings.sync.enabled ? 'idle' : 'off' }
  }

  getRuntime = (): SyncRuntime => this.runtime

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  private setRuntime(next: SyncRuntime): SyncRuntime {
    this.runtime = next
    for (const fn of this.listeners) fn()
    return next
  }

  private get consent() {
    return this.store.getState().settings.sync
  }

  private authed(): Api {
    return this.api.withToken(this.store.getState().sync.token)
  }

  private async ensureIdentity(): Promise<void> {
    if (!this.consent.enabled) throw new Error('Sync is off')
    if (this.store.getState().sync.token) return
    const { token, account_id } = await this.api.createIdentity()
    await this.store.updateSyncMeta({ token, account_id, since: undefined, cursor: undefined })
  }

  // --------------------------------------------------------------- consent

  /** The person said yes. Creates an identity if needed and runs a first full sync. */
  async enable(opts: { shareVotes?: boolean } = {}): Promise<SyncRuntime> {
    await this.store.updateSettings({
      sync: {
        enabled: true,
        consented_at: this.store.stamp(),
        ...(opts.shareVotes !== undefined ? { share_votes: opts.shareVotes } : {}),
      },
    })
    return this.syncNow()
  }

  /**
   * Stop syncing. Data stays on this device. With `forget`, this device also drops its
   * identity, so turning sync on again starts a new account.
   */
  async disable(opts: { forget?: boolean } = {}): Promise<void> {
    await this.store.updateSettings({ sync: { enabled: false, share_votes: false } })
    if (opts.forget) {
      await this.store.updateSyncMeta((m) => ({ dirty: m.dirty }))
    }
    this.setRuntime({ status: 'off' })
  }

  async setShareVotes(on: boolean): Promise<void> {
    await this.store.updateSettings({ sync: { share_votes: on } })
    if (on) await this.shareAndFetchRatings()
  }

  // --------------------------------------------------------------- pairing

  /** A code to type on the other device. Needs sync on. */
  async pairCode(): Promise<string> {
    await this.ensureIdentity()
    const { code } = await this.authed().pairCode()
    return code
  }

  /**
   * Join another device's account. Typing the code is the yes: this turns sync on, then
   * pulls everything from the account and pushes everything from here.
   */
  async link(code: string): Promise<SyncRuntime> {
    const { token, account_id } = await this.api.link(code.trim())
    await this.store.updateSyncMeta({
      token,
      ...(account_id ? { account_id } : {}),
      since: undefined,
      cursor: undefined,
    })
    await this.store.updateSettings({
      sync: { enabled: true, consented_at: this.store.stamp() },
    })
    return this.syncNow()
  }

  // ------------------------------------------------------------------ sync

  /** One round. Concurrent calls share the round in flight. */
  syncNow(): Promise<SyncRuntime> {
    if (!this.running) {
      this.running = this.round().finally(() => {
        this.running = null
      })
    }
    return this.running
  }

  private async round(): Promise<SyncRuntime> {
    if (!this.consent.enabled) return this.setRuntime({ status: 'off' })
    this.setRuntime({ ...this.runtime, status: 'syncing' })
    try {
      await this.ensureIdentity()
      const meta = this.store.getState().sync
      const firstSync = !meta.since
      const toPush = slices(this.store.pendingRecords(firstSync), MAX_PUSH)
      let since = meta.since
      let cursor = meta.cursor
      /** The first page's server_time: the next round's `since`, once a pull completes. */
      let firstTime: string | undefined
      let pulled = 0
      let pushed = 0
      let rejected: string | undefined
      // Every slice goes out (the first with the first pull page, the rest with the pages
      // after it); pull pages without a push stop at MAX_PAGES.
      for (let pullPages = 0; ; ) {
        const sent = toPush.shift() ?? []
        const res = await this.authed().sync({
          ...(since ? { since } : {}),
          ...(cursor ? { cursor } : {}),
          push: sent,
        })
        firstTime ??= res.server_time
        pulled += await this.store.applyRemote(res.records ?? [])
        const settled = await this.settle(sent, res.results)
        pushed += settled.accepted
        rejected ??= settled.rejected
        cursor = res.cursor || undefined
        if (!cursor && firstTime) since = firstTime
        if (sent.length === 0) pullPages++
        if (toPush.length === 0 && (!cursor || pullPages >= MAX_PAGES)) break
      }
      await this.store.updateSyncMeta({
        ...(since ? { since } : {}),
        cursor,
        last_synced_at: this.store.stamp(),
        last_error: rejected,
      })
      await this.shareAndFetchRatings()
      if (rejected) return this.setRuntime({ status: 'error', error: rejected, pulled, pushed })
      return this.setRuntime({ status: 'idle', pulled, pushed })
    } catch (err) {
      const offline = err instanceof ApiError && err.offline
      const message = err instanceof Error ? err.message : String(err)
      if (!offline) await this.store.updateSyncMeta({ last_error: message })
      return this.setRuntime({ status: offline ? 'offline' : 'error', error: message })
    }
  }

  /**
   * Act on the server's answer to one push. `results[i]` answers `sent[i]`; a server that
   * sends no results took everything.
   */
  private async settle(
    sent: SyncRecord[],
    results: PushResult[] | undefined,
  ): Promise<{ accepted: number; rejected?: string }> {
    if (sent.length === 0) return { accepted: 0 }
    const synced: SyncRecord[] = []
    const retry: SyncRecord[] = []
    const copies: { sent: SyncRecord; record: SyncRecord }[] = []
    let rejected: string | undefined
    sent.forEach((record, i) => {
      const result = results?.[i]
      if (!result) return void synced.push(record)
      if (result.outcome === 'failed' || result.outcome === 'rejected') {
        retry.push(record)
        if (result.outcome === 'rejected') rejected ??= rejectionMessage(result)
        return
      }
      synced.push(record)
      if (result.record) copies.push({ sent: record, record: result.record })
    })
    if (copies.length > 0) await this.store.applyServerCopies(copies)
    await this.store.settlePush(synced, retry)
    return { accepted: synced.length, ...(rejected ? { rejected } : {}) }
  }

  /** Votes up (if sharing), community ratings down (if any consent). Never throws. */
  async shareAndFetchRatings(regions?: RegionCode[]): Promise<void> {
    const consent = this.consent
    if (!consent.enabled && !consent.share_votes) return
    try {
      if (consent.share_votes) await this.pushVotes()
      for (const region of regions ?? this.regionsOfInterest()) {
        const ratings = await this.api.communityRatings(region)
        await this.store.setCommunity(region, ratings)
      }
    } catch (err) {
      console.warn('[resupport] community ratings', err)
    }
  }

  private async pushVotes(): Promise<void> {
    const state = this.store.getState()
    const after = timeOf(state.sync.votes_pushed_at)
    const votes: AvailabilityVote[] = []
    for (const r of state.ratings.values()) {
      if (r.deleted || timeOf(r.updated_at) <= after) continue
      votes.push({
        category: r.category,
        region: r.region,
        level: r.level,
        updated_at: r.updated_at,
        ...(r.food ? { food: r.food } : {}),
        ...(r.price ? { price: r.price } : {}),
        ...(r.note ? { note: r.note } : {}),
      })
    }
    if (votes.length === 0) return
    const latest = votes.reduce((m, v) => Math.max(m, timeOf(v.updated_at)), after)
    await this.authed().pushVotes(votes)
    await this.store.updateSyncMeta({ votes_pushed_at: new Date(latest).toISOString() })
  }

  /** Home region and the regions of sections not yet walked, as countries. */
  private regionsOfInterest(): RegionCode[] {
    const state = this.store.getState()
    const out = new Set<RegionCode>()
    const add = (code: RegionCode | undefined) => {
      const r = findRegion(this.store.catalog().regions, code)
      if (r) out.add(r.code)
      else if (code) out.add(code.split('-')[0] ?? code)
    }
    add(this.store.resolvedSettings().region)
    for (const trip of state.trips.values()) {
      if (trip.deleted) continue
      for (const s of trip.sections) if (s.status !== 'walked') add(s.region ?? trip.region)
    }
    return [...out].slice(0, 8)
  }

  // ------------------------------------------------------------ automation

  /**
   * Sync when it makes sense: as soon as the store has loaded (and whenever sync is turned
   * on), on coming back online, on returning to the app, a few seconds after local changes,
   * and every few minutes. Each trigger re-checks consent, and none fires before the store has
   * loaded: until then the settings read as the defaults, with sync off.
   */
  start(opts: { intervalMs?: number; debounceMs?: number } = {}): () => void {
    const interval = opts.intervalMs ?? 5 * 60_000
    const debounce = opts.debounceMs ?? 4_000
    let stopped = false
    const kick = () => {
      if (!stopped && this.store.getState().ready && this.consent.enabled) void this.syncNow()
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    let lastDirty = this.store.getState().sync.dirty
    let wasOn = this.store.getState().ready && this.consent.enabled
    const unsub = this.store.subscribe(() => {
      const { sync, settings, ready } = this.store.getState()
      const dirty = sync.dirty
      const on = ready && settings.sync.enabled
      if (!settings.sync.enabled) {
        wasOn = false
        if (this.runtime.status !== 'off') this.setRuntime({ status: 'off' })
        return
      }
      if (on && !wasOn) {
        // The store finished loading with sync on, or sync was just turned on.
        wasOn = true
        lastDirty = dirty
        kick()
        return
      }
      if (dirty === lastDirty) return
      lastDirty = dirty
      if (Object.keys(dirty).length === 0) return
      clearTimeout(timer)
      timer = setTimeout(kick, debounce)
    })
    const onVisible = () => {
      if (document.visibilityState === 'visible') kick()
    }
    const every = setInterval(kick, interval)
    if (typeof window !== 'undefined') window.addEventListener('online', kick)
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible)
    // Already loaded with sync on: go now. Otherwise the subscriber goes when it loads.
    if (wasOn) kick()
    else void this.store.load()
    return () => {
      stopped = true
      unsub()
      clearTimeout(timer)
      clearInterval(every)
      if (typeof window !== 'undefined') window.removeEventListener('online', kick)
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible)
      }
    }
  }
}
